import { Fragment, type ReactNode } from 'react';
import type { BulletNode } from '../types';

/**
 * Split a bullet's text on inline `code` spans (single backticks) and
 * render the code portions as <code>. Falls back to plain text when there
 * are no backticks.
 */
function renderInline(text: string): ReactNode {
  if (!text.includes('`')) return text;

  const parts = text.split(/(`[^`]+`)/g);
  return parts.map((part, i) => {
    if (part.startsWith('`') && part.endsWith('`') && part.length >= 2) {
      return (
        <code
          key={i}
          className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[0.85em] text-indigo-700"
        >
          {part.slice(1, -1)}
        </code>
      );
    }
    return <Fragment key={i}>{part}</Fragment>;
  });
}

function BulletList({ nodes, depth }: { nodes: BulletNode[]; depth: number }) {
  return (
    <ul
      className={
        depth === 0
          ? 'space-y-2'
          : 'mt-1.5 space-y-1.5 border-l border-slate-200 pl-4'
      }
    >
      {nodes.map((node, i) => (
        <li key={i} className="text-sm leading-relaxed text-slate-700">
          <div className="flex gap-2">
            <span
              aria-hidden
              className={`mt-2 h-1.5 w-1.5 shrink-0 rounded-full ${
                depth === 0 ? 'bg-indigo-500' : 'bg-slate-400'
              }`}
            />
            <span>{renderInline(node.text)}</span>
          </div>
          {node.children.length > 0 && (
            <BulletList nodes={node.children} depth={depth + 1} />
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * Renders a version's parsed changelog body (a nested bullet tree) using
 * the app's card styling.
 */
export function ChangelogContent({ body }: { body: BulletNode[] }) {
  if (body.length === 0) {
    return (
      <p className="text-sm text-slate-500">
        No release notes recorded for this version.
      </p>
    );
  }

  return <BulletList nodes={body} depth={0} />;
}
